import { Fragment, useState } from 'react';
import { observer } from 'mobx-react-lite';
import cls from 'classnames';

import FullscreenOff from '@/assets/icons/icon-fullscreen-off.svg?react';
import FullscreenOn from '@/assets/icons/icon-fullscreen-on.svg?react';
import ProportionsAll from '@/assets/icons/icon-proportions-compare-all.svg?react';
import ProportionsLeft from '@/assets/icons/icon-proportions-compare-left.svg?react';
import ProportionsTop from '@/assets/icons/icon-proportions-compare-top.svg?react';
import SVGDiagram from '@/components/SVGDiagram/SVGDiagram';
import { EMPTY_VALUE_DASH } from '@/constants/common';
import store from '@/stores/Store';
import { useStore } from '@/stores/StoreProvider';

import styles from './Proportions.module.scss';

function Proportions() {
  const { diamond } = useStore('diamondsStore');

  const [activeReportImage, setActiveReportImage] = useState(diamond?.reportImages[0]);
  const [isFullscreenEnabled, setFullscreenEnabled] = useState(false);

  function onToggleFullscreen() {
    const isEnabled = !isFullscreenEnabled;

    try {
      if (isEnabled) {
        window.screen.orientation.lock('portrait');
      } else {
        window.screen.orientation.unlock();
      }
    } catch (e) {
      //
    }

    setFullscreenEnabled(isEnabled);
    store.app.setScrollLocked(isEnabled);
  }

  if (!diamond?.reportData) {
    return null;
  }

  return (
    <div className={styles.root}>
      <div className="container">
        <h2 className={styles.title}>Proportions</h2>

        {diamond.reportData.width && diamond.reportData.length && (
          <div>
            {diamond.isRound && (
              <Fragment>
                <span>{diamond.reportData.width}</span>

                <span>&nbsp;-&nbsp;</span>

                <span>{diamond.reportData.length}</span>

                <span>&nbsp;&times;&nbsp;</span>

                <span>{diamond.reportData.height}</span>
              </Fragment>
            )}

            {!diamond.isRound && (
              <Fragment>
                <span>{diamond.reportData.length}</span>

                <span>&nbsp;&times;&nbsp;</span>

                <span>{diamond.reportData.width}</span>

                <span>&nbsp;&times;&nbsp;</span>

                <span>{diamond.reportData.height}</span>
              </Fragment>
            )}

            <span>&nbsp;MM</span>
          </div>
        )}

        <div className={styles.proportions}>
          {diamond.proportionsImg && (
            <div className={cls(styles.diagram, { [styles.fullscreen]: isFullscreenEnabled })}>
              <SVGDiagram className={styles.image} url={diamond.proportionsImg} />

              <div className={styles.toggleFullscreen}>
                <button onClick={onToggleFullscreen}>{isFullscreenEnabled ? <FullscreenOff /> : <FullscreenOn />}</button>
              </div>
            </div>
          )}

          <div className={styles.raw}>
            <div>Table</div>

            <div className={styles.value}>
              <div>{diamond.reportData.tablePc && `${diamond.reportData.tablePc}%`}</div>

              <div className={styles.valueRight}>{diamond.reportData.table ? `${diamond.reportData.table} MM` : EMPTY_VALUE_DASH}</div>
            </div>
          </div>

          <div className={styles.raw}>
            <div>Total Height</div>

            <div className={styles.value}>
              <div className={styles.valueRight}>{diamond.reportData.heightPc ? `${diamond.reportData.heightPc}%` : EMPTY_VALUE_DASH}</div>
            </div>
          </div>

          <div className={styles.raw}>
            <div>Girdle</div>

            <div className={styles.value}>
              <div>{diamond.reportData.girdleBezelPc && `${diamond.reportData.girdleBezelPc}%`}</div>

              <div className={styles.valueRight}>{diamond.reportData.girdleBezel ? `${diamond.reportData.girdleBezel} MM` : EMPTY_VALUE_DASH}</div>
            </div>
          </div>

          <div className={styles.raw}>
            <div>Crown Angle</div>

            <div className={styles.value}>{diamond.reportData.crnAn ? <span>{diamond.reportData.crnAn}&deg;</span> : EMPTY_VALUE_DASH}</div>
          </div>

          <div className={styles.raw}>
            <div>Pavilion Angle</div>

            <div className={styles.value}>{diamond.reportData.pavAn ? <span>{diamond.reportData.pavAn}&deg;</span> : EMPTY_VALUE_DASH}</div>
          </div>
        </div>

        {diamond.reportImages.length > 0 && activeReportImage && (
          <div className={styles.report}>
            <div
              className={cls(styles.reportDiagram, {
                [styles.reportPlotBothAxesSymCor]: Boolean(activeReportImage?.type === 'PlotBothAxesSymCor'),
              })}
            >
              <SVGDiagram url={activeReportImage.url} />
            </div>

            {diamond.reportImages.length > 1 && (
              <div className={styles.reportPreviews}>
                {diamond.reportImages.map((reportImage) => (
                  <div
                    key={reportImage.type}
                    className={cls(styles.preview, {
                      [styles.previewActive]: activeReportImage?.type === reportImage.type,
                    })}
                  >
                    <div className={styles.previewWrapper}>
                      <div className={styles.previewWrapperContent} onClick={() => setActiveReportImage(reportImage)}>
                        {reportImage.type === 'PlotActualWidthAxisAsym' && (
                          <div className={styles.previewImage}>
                            <ProportionsTop />
                          </div>
                        )}

                        {reportImage.type === 'PlotActualLengthAxisAsym' && (
                          <div className={styles.previewImage}>
                            <ProportionsLeft />
                          </div>
                        )}

                        {reportImage.type === 'PlotBothAxesSymCor' && (
                          <div className={styles.previewImage}>
                            <ProportionsAll />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
}

export default observer(Proportions);
